import axios, { AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { message } from 'ant-design-vue'

const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    // The web's refresh session: 14 days idle, a month at most (security audit 2026-10-07, A6). The server goes by
    // what the client names.
    'X-Client-Platform': 'web',
  },
})

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

// A refused sign-in or refresh is an answer, not an expired access token.
const isAuthEndpoint = (url?: string) => /\/auth\/(login|refresh)$/i.test(url ?? '')

// One refresh shared by every request that fails with 401 while it runs: refresh tokens are one-use, and a second
// refresh with the same token would end the session.
let refreshing: Promise<string> | null = null

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('admin_refresh_token')
  if (!refreshToken) throw new Error('No refresh token')
  const response = await apiClient.post<{ accessToken: string; refreshToken: string }>('/Auth/refresh', { refreshToken })
  localStorage.setItem('admin_token', response.data.accessToken)
  localStorage.setItem('admin_refresh_token', response.data.refreshToken)
  return response.data.accessToken
}

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('admin_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// The API wraps every 2xx body in { success, statusCode, message, data, metadata }
// (ApiResponseMiddleware). Unwrap it once here so callers always receive the payload
// itself — previously only the paginated endpoints unwrapped, and every single-item
// call silently handed the envelope object back to the UI as if it were the resource.
const isApiEnvelope = (body: unknown): body is { data: unknown } =>
  typeof body === 'object' &&
  body !== null &&
  'success' in body &&
  'statusCode' in body &&
  'data' in body

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (isApiEnvelope(response.data)) {
      response.data = response.data.data
    }
    return response
  },
  async (error: AxiosError) => {
    // Access tokens live an hour (A6): the first 401 refreshes once and retries before anyone is signed out.
    const original = error.config as RetriableConfig | undefined
    if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint(original.url)) {
      original._retry = true
      try {
        const accessToken = await (refreshing ??= refreshAccessToken().finally(() => {
          refreshing = null
        }))
        original.headers.Authorization = `Bearer ${accessToken}`
        return apiClient(original)
      } catch {
        // The session is over: signed out below.
      }
    }

    if (error.response) {
      switch (error.response.status) {
        case 401:
          message.error('Session expired. Please login again.')
          localStorage.removeItem('admin_token')
          localStorage.removeItem('admin_refresh_token')
          window.location.href = '/login'
          break
        case 403:
          message.error('Access denied. Insufficient permissions.')
          break
        case 404:
          message.error('Resource not found.')
          break
        case 500:
          message.error('Server error. Please try again later.')
          break
        default:
          message.error((error.response.data as any)?.message || 'An error occurred')
      }
    } else if (error.request) {
      message.error('Network error. Please check your connection.')
    } else {
      message.error('Request failed. Please try again.')
    }
    return Promise.reject(error)
  }
)

export default apiClient
